<template>
  <div class="antrittskontrolle">
    <div class="split-button d-flex align-center full-width">
      <div
        @click="signIn"
        v-ripple
        class="d-flex flex-grow-1 justify-center align-center full-height"
        :class="[signInButtonStates[signInButtonState].class, !!userState.length ? 'split-button__antrittskontrolle' : 'split-button__antrittskontrolle--solo']"
        :style="{ cursor: loading ? 'wait' : 'pointer' }"
      >
        <div class="d-flex justify-center align-center">
          <img-icon
            v-if="signInButtonStates[signInButtonState].iconType == 'img'"
            :icon="signInButtonStates[signInButtonState].iconClass"
            class="pr-3 mb-1"
          ></img-icon>
          <v-icon color="white" v-if="signInButtonStates[signInButtonState].iconType == 'mdi'" class="pr-3">{{
            signInButtonStates[signInButtonState].iconClass
          }}</v-icon>
          <p class="ma-0">Antrittskontrolle</p>
        </div>
      </div>
      <button v-if="!!userState.length" v-ripple @click="isDialogOpen = true" class="split-button__dialog full-height">
        <v-badge v-if="unreadUserStateCount" overlap :color="theme.unreadBadgeColor" :content="unreadUserStateCount">
          <v-icon color="black">mdi-list-box-outline</v-icon>
        </v-badge>
        <v-icon v-else color="black">mdi-list-box-outline</v-icon>
      </button>
    </div>
    <div class="text-caption text-center mt-2 white--text">Letzte Anmeldung: {{ lastSignIn }}</div>
    <v-snackbar v-model="snackBar" :timeout="3000" :color="snackBarColor" centered text outlined>
      <v-icon :color="snackBarColor" class="pr-2">{{ snackBarIcon }}</v-icon>
      {{ snackBarMessage }}
    </v-snackbar>
    <v-dialog v-model="isDialogOpen" max-width="700">
      <v-card>
        <v-card-title class="pr-3 pl-5">
          Antrittskontrolle
          <v-spacer></v-spacer>
          <v-btn icon @click="isDialogOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <div
          v-for="state in userState"
          :key="`user-state-${state.id}`"
          class="bottom-border d-flex justify-space-between align-center ml-5 mr-4 mt-3 pb-3 mb-2"
        >
          <div>
            <p class="caption ma-0 font-weight-light">{{ formattedDate(state.date) }}</p>
            <v-card-text class="pa-0">
              {{ state.text }}
            </v-card-text>
          </div>
          <v-btn @click="confirmRead(state)" :disabled="!!state.dateread" :color="!!state.dateread ? '' : 'accent'" outlined class="font-weight-black">{{
            state.dateread ? $t('confirmed') : $t('confirm')
          }}</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import common from '@/mixins/common';
import configMixin from '@/mixins/configMixin';
import ImgIcon from '@/components/icons/ImgIcon';

import dateUtils from '@/common/dateUtils';

export default {
  name: 'ThurboAnmeldung',
  mixins: [common, configMixin],
  components: {
    ImgIcon,
  },
  props: {
    config: {
      type: Object,
    },
  },
  data() {
    return {
      isDialogOpen: false,
      loading: false,
      lastSignIn: 'Noch nie',
      snackBar: false,
      snackBarIcon: 'mdi-alert-circle',
      snackBarColor: 'error',
      snackBarMessage: 'Die Anmeldung ist fehlgeschlagen.',
      signInButtonState: 'default',
      signInButtonStates: {
        default: {
          class: 'signInButton',
          iconType: 'img',
          iconClass: this.config.icon,
        },
        loading: {
          class: 'grey',
          iconType: 'mdi',
          iconClass: 'mdi-loading mdi-spin',
        },
        success: {
          class: 'light-green accent-4',
          iconType: 'img',
          iconClass: this.config.icon,
        },
        error: {
          class: 'orange',
          iconType: 'mdi',
          iconClass: 'mdi-alert',
        },
      },
      userState: [],
      isOnline: navigator.onLine,
    };
  },
  async mounted() {
    this.getLastSignIn();

    if (navigator.onLine) {
      this.userState = await this.getUserState();
      this.setUserStateInLocalStorage();
    } else {
      this.userState = this.getUserStateFromLocalStorage();
    }

    window.addEventListener('online', async (e) => {
      this.isOnline = true;
      for (const state of this.userState) {
        if (state.isNotSent) {
          await axios.post('/widgets/thurboAnmeldung/confirmRead', {
            userStateId: state.id,
            dateRead: state.dateread,
            adapterId: state.adapter_id,
          });
          delete state.isNotSent;
        }
      }
      this.setUserStateInLocalStorage();
    });

    window.addEventListener('offline', (e) => {
      this.isOnline = false;
    });
  },
  beforeDestroy() {
    window.removeEventListener('online', () => {});
    window.removeEventListener('offline', () => {});
  },
  computed: {
    unreadUserStateCount() {
      return this.userState.filter((state) => !state.dateread).length;
    },
    wereAllUserStatesRead() {
      return this.userState.every((state) => state.dateread);
    },
  },
  methods: {
    /**
     *
     * @param {Date} date
     * @return {string}
     */
    formattedDate(date) {
      return dateUtils.reformatDateIsoStringAsHHmmDDMMYYYY(date);
    },
    async signIn() {
      if (this.loading) {
        return;
      }
      this.loading = true;
      this.signInButtonState = 'loading';
      if (!this.isOnline) {
        this.snackBarMessage = 'Sie sind zur Zeit offline.';
        this.snackBarColor = 'error';
        this.snackBarIcon = 'mdi-access-point-off';
        this.signInButtonState = 'error';
      } else {
        let response = await axios.get('/widgets/thurboAnmeldung/?nocache');
        if (response && response.data && response.data.success) {
          // success
          this.setLastSignIn();
          this.userState = await this.getUserState();
          this.setUserStateInLocalStorage();
          this.snackBarMessage = 'Anmeldung erfolgreich';
          this.snackBarColor = 'success';
          this.snackBarIcon = 'mdi-check-circle';
          this.signInButtonState = 'success';
          if (!this.wereAllUserStatesRead) {
            this.isDialogOpen = true;
          }
        } else {
          // error
          this.snackBarMessage = response && response.data && response.data.error ? response.data.error.text : 'Die Anmeldung ist fehlgeschlagen.';
          this.snackBarColor = 'error';
          this.snackBarIcon = 'mdi-alert';
          this.signInButtonState = 'error';
        }
      }
      this.snackBar = true;
      this.loading = false;
    },
    getLastSignIn() {
      const lastSignIn = localStorage.getItem('thurboAnmeldung');
      this.lastSignIn = lastSignIn ? lastSignIn : 'Noch nie';
    },
    setLastSignIn() {
      const now = new Date();
      const lastSignInDate = now.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const lastSignInTime = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
      const lastSignIn = lastSignInDate + ' ' + lastSignInTime;
      localStorage.setItem('thurboAnmeldung', lastSignIn);
      this.getLastSignIn();
    },
    async getUserState() {
      const response = await axios.get('/widgets/thurboAnmeldung/userState?nocache');
      return response && response.data && response.data.anmeldungUserState ? response.data.anmeldungUserState : [];
    },
    async confirmRead(userState) {
      const now = new Date();
      userState.dateread = now;
      if (this.isOnline) {
        await axios.post('/widgets/thurboAnmeldung/confirmRead', { userStateId: userState.id, dateRead: now, adapterId: userState.adapter_id });
      } else {
        userState.isNotSent = true;
      }
      this.setUserStateInLocalStorage();

      // close dialog when all user states were read
      this.isDialogOpen = !this.wereAllUserStatesRead;
    },
    setUserStateInLocalStorage() {
      return localStorage.setItem('thurboAnmeldungUserState', JSON.stringify(this.userState));
    },
    getUserStateFromLocalStorage() {
      const userState = localStorage.getItem('thurboAnmeldungUserState');
      return userState ? JSON.parse(userState) : [];
    },
  },
};
</script>

<style scoped>
.antrittskontrolle {
  width: 100%;
  max-width: 37.5vw;
  margin-top: 16px;
}

@media screen and (max-width: 599px) {
  .antrittskontrolle {
    width: calc(100% - 24px);
    max-width: 100%;
    margin: 0 12px;
  }
}

.bottom-border:not(:last-child) {
  border-bottom: 1px solid lightgrey;
}

.split-button {
  height: 50px;
}

.split-button p {
  font-weight: bold;
  color: white;
}

.split-button__antrittskontrolle {
  color: white;
  border-radius: 16px 0 0 16px;
}

.split-button__antrittskontrolle--solo {
  color: white;
  border-radius: 16px;
}

.split-button__dialog {
  width: 80px;
  border-radius: 0 16px 16px 0;
  background-color: white;
}

.signInButton {
  background-color: #009459 !important;
}
</style>
